import React, { useState, useContext, useMemo, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { PlusIcon, TrashIcon, EyeIcon, TruckIcon } from '@heroicons/react/24/outline';
import useApi from '../hooks/useApi'
import { GlobalContext } from '../context/GlobalContext';
import FleetDashboard from '../components/FleetDashboard';
import haxios from '../lib/haxios';
import '../css/dashboard.css'; // Corrected import path



// Helper function to format mileage
function formatMileage(mileage) {
    if (!mileage) return 'N/A';
    return mileage.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

// Helper function to format vehicle condition
function formatCondition(condition) {
    if (!condition) return 'N/A';
    return condition.charAt(0).toUpperCase() + condition.slice(1).toLowerCase();
}

// Helper function to format tax status
function formatTaxStatus(status) {
    if (!status) return 'N/A';
    return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
}

function Chip({ children, color }) {
    const base = 'px-2 py-1 rounded-full text-xs font-semibold';
    const colors = {
        red: 'bg-red-100 text-red-800',
        green: 'bg-green-100 text-green-800',
        yellow: 'bg-yellow-100 text-yellow-800',
        gray: 'bg-gray-100 text-gray-800',
    };
    return <span className={`${base} ${colors[color] || colors.gray}`}>{children}</span>;
}

function isDateExpired(dateStr) {
    if (!dateStr) return false;
    const today = new Date();
    const date = new Date(dateStr);
    return date < today.setHours(0, 0, 0, 0);
}
function isDateWithinDays(dateStr, days) {
    if (!dateStr) return false;
    const today = new Date();
    const date = new Date(dateStr);
    const diff = (date - today) / (1000 * 60 * 60 * 24);
    return diff >= 0 && diff <= days;
}

function timeAgo(dateStr) {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays < 1) return 'Today';
    if (diffDays === 1) return '1 day ago';
    if (diffDays < 7) return `${diffDays} days ago`;
    const diffWeeks = Math.floor(diffDays / 7);
    if (diffWeeks === 1) return '1 week ago';
    if (diffWeeks < 5) return `${diffWeeks} weeks ago`;
    const diffMonths = Math.floor(diffDays / 30);
    if (diffMonths === 1) return '1 month ago';
    if (diffMonths < 12) return `${diffMonths} months ago`;
    return 'over a year ago';
}

export default function Vehicles() {
    const location = useLocation();
    const { data: vehicles, loading, refetch } = useApi({ url: `/visiondashboard/salesassist/vehicles`, initial: [] });
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedBranch, setSelectedBranch] = useState('');
    const [sortField, setSortField] = useState('');
    const [sortDirection, setSortDirection] = useState('asc');

    // Set document title
    useEffect(() => {
        document.title = 'Fleet Vehicles - Sales Assist';
        return () => {
            document.title = 'Sales Assist';
        };
    }, []);

    // Determine the current fleet section based on the URL
    const currentSection = location.pathname.split('/')[2] || 'vehicles';

    // Get unique branches for selector
    const branches = useMemo(() => {
        const all = vehicles?.data?.map(v => v.branch).filter(Boolean) || [];
        return Array.from(new Set(all));
    }, [vehicles]);

    // Calculate KPIs
    const kpis = useMemo(() => {
        const vehicleList = vehicles?.data || [];
        const totalVehicles = vehicleList.length;
        const expiredMOT = vehicleList.filter(v => {
            const motDate = (v.nextMot || '').split('T')[0];
            return isDateExpired(motDate);
        }).length;
        const expiredInsurance = vehicleList.filter(v => {
            const insDate = (v.insuredExpire || '').split('T')[0];
            return isDateExpired(insDate);
        }).length;
        const untaxed = vehicleList.filter(v => {
            const status = (v.taxStatus || '').toLowerCase();
            return status === 'untaxed';
        }).length;
        return { totalVehicles, expiredMOT, expiredInsurance, untaxed };
    }, [vehicles]);

    // Filter by search and branch
    const filteredVehicles = vehicles?.data?.filter((vehicle) => {
        const matchesSearch = vehicle.registration.toLowerCase().includes(searchQuery.toLowerCase()) ||
            vehicle.make.toLowerCase().includes(searchQuery.toLowerCase()) ||
            vehicle.model.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesBranch = selectedBranch ? vehicle.branch === selectedBranch : true;
        return matchesSearch && matchesBranch;
    }) || [];

    // Sort vehicles
    const sortedVehicles = [...filteredVehicles].sort((a, b) => {
        const aValue = a[sortField] || '';
        const bValue = b[sortField] || '';
        if (sortDirection === 'asc') {
            return aValue.localeCompare(bValue);
        } else {
            return bValue.localeCompare(aValue);
        }
    });

    const [currentPage, setCurrentPage] = useState(1);
    const vehiclesPerPage = 5;
    const paginatedVehicles = sortedVehicles.slice((currentPage - 1) * vehiclesPerPage, currentPage * vehiclesPerPage);

    const handleDeleteVehicle = async (vehicleId) => {
        if (!window.confirm('Are you sure you want to delete this vehicle?')) {
            return;
        }

        try {
            await haxios.delete(`/visiondashboard/salesassist/vehicles/${vehicleId}`);
            refetch(); // Refresh the vehicles list
        } catch (error) {
            console.error('Error deleting vehicle:', error);
            alert('Failed to delete vehicle. Please try again.');
        }
    };

    const handleSort = (field) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
        setCurrentPage(1); // Reset to first page when sorting
    };

    const getSortIcon = (field) => {
        if (sortField !== field) return '↕️';
        return sortDirection === 'asc' ? '↑' : '↓';
    };

    // Render different content based on the current section
    const renderContent = () => {
        switch (currentSection) {
            case 'incidents':
                return (
                    <div className="text-center py-8">
                        <h2 className="text-2xl font-bold text-gray-900 mb-4">Fleet Incidents</h2>
                        <p className="text-gray-600">Incident management functionality coming soon...</p>
                    </div>
                );
            case 'fuel':
                return (
                    <div className="text-center py-8">
                        <h2 className="text-2xl font-bold text-gray-900 mb-4">Fuel Logs</h2>
                        <p className="text-gray-600">Fuel log management functionality coming soon...</p>
                    </div>
                );
            case 'drivers':
                return (
                    <div className="text-center py-8">
                        <h2 className="text-2xl font-bold text-gray-900 mb-4">Fleet Drivers</h2>
                        <p className="text-gray-600">Driver management functionality coming soon...</p>
                    </div>
                );
            default:
                return (
                    <>
                        {/* Header */}
                        <div className="bg-white shadow-sm border-b border-gray-200 px-6 py-4 mb-6 -mx-6">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-3">
                                    <div className="p-2 rounded-lg bg-blue-100">
                                        <TruckIcon className="w-6 h-6 text-blue-600" />
                                    </div>
                                    <div>
                                        <h1 className="text-2xl font-bold text-gray-900">Fleet Vehicles</h1>
                                        <p className="text-sm text-gray-600 mt-1">Manage and track your fleet vehicles</p>
                                    </div>
                                </div>
                                <Link 
                                    to="/fleet/vehicles/add" 
                                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                                >
                                    <PlusIcon className="w-5 h-5" /> Add Vehicle
                                </Link>
                            </div>
                        </div>

                        {/* KPI Cards */}
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                            <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm border-l-4 border-l-blue-500">
                                <div className="text-sm font-medium text-gray-600 mb-1">Total Vehicles</div>
                                <div className="text-2xl font-bold text-gray-900">{kpis.totalVehicles.toLocaleString('en-GB')}</div>
                                <div className="text-xs text-gray-500 mt-1">All vehicles</div>
                            </div>
                            <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm border-l-4 border-l-red-500">
                                <div className="text-sm font-medium text-gray-600 mb-1">Expired MOT</div>
                                <div className="text-2xl font-bold text-gray-900">{kpis.expiredMOT.toLocaleString('en-GB')}</div>
                                <div className="text-xs text-gray-500 mt-1">Requires attention</div>
                            </div>
                            <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm border-l-4 border-l-orange-500">
                                <div className="text-sm font-medium text-gray-600 mb-1">Expired Insurance</div>
                                <div className="text-2xl font-bold text-gray-900">{kpis.expiredInsurance.toLocaleString('en-GB')}</div>
                                <div className="text-xs text-gray-500 mt-1">Requires attention</div>
                            </div>
                            <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm border-l-4 border-l-yellow-500">
                                <div className="text-sm font-medium text-gray-600 mb-1">Untaxed</div>
                                <div className="text-2xl font-bold text-gray-900">{kpis.untaxed.toLocaleString('en-GB')}</div>
                                <div className="text-xs text-gray-500 mt-1">Requires attention</div>
                            </div>
                        </div>

                        {/* Filters */}
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
                            <div className="flex flex-wrap items-end gap-4">
                                <div className="flex-1 min-w-[200px]">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
                                    <input
                                        type="text"
                                        placeholder="Search by reg, make, model..."
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                    />
                                </div>
                                <div className="min-w-[180px]">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Branch</label>
                                    <select
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        value={selectedBranch}
                                        onChange={e => setSelectedBranch(e.target.value)}
                                    >
                                        <option value="">All Branches</option>
                                        {branches.map(branch => (
                                            <option key={branch} value={branch}>{branch}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>

                        {/* Dashboard */}
                        <FleetDashboard vehicles={filteredVehicles} />

                        {/* Vehicles Table */}
                        {loading ? (
                            <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-12 text-center">
                                <p className="text-gray-600">Loading vehicles...</p>
                            </div>
                        ) : (
                            <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-50">
                                        <tr>
                                            <th
                                                className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                                onClick={() => handleSort('branch')}
                                            >
                                                Branch {getSortIcon('branch')}
                                            </th>
                                            <th
                                                className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                                onClick={() => handleSort('registration')}
                                            >
                                                Registration {getSortIcon('registration')}
                                            </th>
                                            <th
                                                className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                                onClick={() => handleSort('make')}
                                            >
                                                Make {getSortIcon('make')}
                                            </th>
                                            <th
                                                className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                                onClick={() => handleSort('model')}
                                            >
                                                Model {getSortIcon('model')}
                                            </th>
                                            <th
                                                className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                                onClick={() => handleSort('year')}
                                            >
                                                Year {getSortIcon('year')}
                                            </th>
                                            <th
                                                className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                                onClick={() => handleSort('vehicleCondition')}
                                            >
                                                Condition {getSortIcon('vehicleCondition')}
                                            </th>
                                            <th
                                                className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                                onClick={() => handleSort('nextMot')}
                                            >
                                                MOT {getSortIcon('nextMot')}
                                            </th>
                                            <th
                                                className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                                onClick={() => handleSort('taxStatus')}
                                            >
                                                Tax {getSortIcon('taxStatus')}
                                            </th>
                                            <th
                                                className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                                onClick={() => handleSort('insuredExpire')}
                                            >
                                                Insurance {getSortIcon('insuredExpire')}
                                            </th>
                                            <th
                                                className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                                onClick={() => handleSort('mileage')}
                                            >
                                                Mileage {getSortIcon('mileage')}
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {paginatedVehicles.length > 0 ? (
                                            paginatedVehicles.map((vehicle) => (
                                                <tr key={vehicle.id} className="hover:bg-gray-50 transition-colors">
                                                    <td className="px-4 py-3 text-sm text-gray-900">{vehicle.branch || 'N/A'}</td>
                                                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{vehicle.registration || 'N/A'}</td>
                                                    <td className="px-4 py-3 text-sm text-gray-900">{vehicle.make || 'N/A'}</td>
                                                    <td className="px-4 py-3 text-sm text-gray-900">{vehicle.model || 'N/A'}</td>
                                                    <td className="px-4 py-3 text-sm text-gray-900">{vehicle.year || 'N/A'}</td>
                                                    <td className="px-4 py-3 text-sm">
                                                        {(() => {
                                                            const cond = (vehicle.vehicleCondition || '').toLowerCase();
                                                            if (cond === 'poor') return <Chip color="red">Poor</Chip>;
                                                            if (cond === 'excellent') return <Chip color="green">Excellent</Chip>;
                                                            if (cond === 'fair' || cond === 'average') return <Chip color="yellow">{formatCondition(vehicle.vehicleCondition)}</Chip>;
                                                            return <Chip color="gray">{formatCondition(vehicle.vehicleCondition)}</Chip>;
                                                        })()}
                                                    </td>
                                                    <td className="px-4 py-3 text-sm">
                                                        {(() => {
                                                            const date = (vehicle.nextMot || '').split('T')[0];
                                                            if (isDateExpired(date)) return <Chip color="red">{date}</Chip>;
                                                            if (isDateWithinDays(date, 60)) return <Chip color="yellow">{date}</Chip>;
                                                            return <Chip color="gray">{date || 'N/A'}</Chip>;
                                                        })()}
                                                    </td>
                                                    <td className="px-4 py-3 text-sm">
                                                        {(() => {
                                                            const status = (vehicle.taxStatus || '').toLowerCase();
                                                            if (status === 'taxed') return <Chip color="green">Taxed</Chip>;
                                                            if (status === 'untaxed') return <Chip color="red">Untaxed</Chip>;
                                                            return <Chip color="gray">{formatTaxStatus(vehicle.taxStatus)}</Chip>;
                                                        })()}
                                                    </td>
                                                    <td className="px-4 py-3 text-sm">
                                                        {(() => {
                                                            const date = (vehicle.insuredExpire || '').split('T')[0];
                                                            if (isDateExpired(date)) return <Chip color="red">{date}</Chip>;
                                                            if (isDateWithinDays(date, 60)) return <Chip color="yellow">{date}</Chip>;
                                                            return <Chip color="gray">{date || 'N/A'}</Chip>;
                                                        })()}
                                                    </td>
                                                    <td className="px-4 py-3 text-sm text-gray-900">{formatMileage(vehicle.mileage)}</td>
                                                    <td className="px-4 py-3 text-sm">
                                                        <div className="flex items-center gap-2">
                                                            <Link 
                                                                to={`/fleet/vehicles/${vehicle.id}`} 
                                                                className="text-blue-600 hover:text-blue-800 transition-colors"
                                                            >
                                                                <EyeIcon className="w-5 h-5" />
                                                            </Link>
                                                            <button
                                                                onClick={() => handleDeleteVehicle(vehicle.id)}
                                                                className="text-red-600 hover:text-red-800 transition-colors"
                                                            >
                                                                <TrashIcon className="w-5 h-5" />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="11" className="px-4 py-8 text-center text-gray-500">No vehicles found.</td>
                                            </tr>
                                        )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                        {/* Add pagination logic and controls after the table */}
                        <div className="flex justify-center mt-4">
                            <button
                                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                                disabled={currentPage === 1}
                                className="px-4 py-2 mx-1 bg-gray-200 rounded disabled:opacity-50"
                            >
                                Previous
                            </button>
                            <span className="px-4 py-2 mx-1">Page {currentPage} of {Math.ceil(filteredVehicles.length / vehiclesPerPage)}</span>
                            <button
                                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, Math.ceil(filteredVehicles.length / vehiclesPerPage)))}
                                disabled={currentPage === Math.ceil(filteredVehicles.length / vehiclesPerPage) || filteredVehicles.length === 0}
                                className="px-4 py-2 mx-1 bg-gray-200 rounded disabled:opacity-50"
                            >
                                Next
                            </button>
                        </div>
                    </>
                );
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 pt-20">
            <div className="py-6">
                <div className="px-6">
                    {renderContent()}
                </div>
            </div>
        </div>
    );
}
